import { BenefitItemVariant, InsurancePlan, InsurancePlanBenefitItem, InsurancePlanType } from '@typesApp/InsurancePlan'
import {
  checkBenefitServiceHasCopay,
  getAdditionalPurchaseActiveBenefit,
  getBenefitServiceDescriptionInfo,
  getRoutineActiveBenefit,
} from './benefitPlanUtil'

type BaseDetails = {
  isRenewal?: boolean
  isStatusAvailable?: boolean
  isCustomerServiceOnly?: boolean
  isComingSoon?: boolean
  isCopay?: boolean
  serviceDescription?: string
  showFallbackDescription?: boolean
}

export type BenefitDetails = BaseDetails & {
  benefitItem: InsurancePlanBenefitItem | undefined
}

const EMPTY_DETAILS: BaseDetails = {
  serviceDescription: '',
}

/**
 * @returns benefits details for the benefit plan
 */
export const getBenefitPlanDetails = (plan: InsurancePlan, variant: BenefitItemVariant): BenefitDetails => {
  return variant === 'routine' ? getRoutineBenefitDetails(plan) : getAdditionalPurchaseBenefitDetails(plan)
}

const getRoutineBenefitDetails = (plan: InsurancePlan): BenefitDetails => {
  const { planType } = plan
  const benefitItem = getRoutineActiveBenefit(plan)
  let details = EMPTY_DETAILS

  switch (planType) {
    case 'FRAME':
      details = getFrameBenefitDetails(planType, benefitItem)
      break
    case 'LENSES':
      details = getLensesBenefitDetails(planType, benefitItem)
      break
    case 'EYE_EXAM':
      details = getEyeExamBenefitDetails(planType, benefitItem)
      break
    case 'CONTACT_LENSES':
    default:
      details = getGenericBenefitDetails(planType, benefitItem)
      break
  }

  return {
    ...details,
    benefitItem,
  }
}

const getAdditionalPurchaseBenefitDetails = (plan: InsurancePlan): BenefitDetails => {
  const { planType } = plan
  const benefitItem = getAdditionalPurchaseActiveBenefit(plan)
  let details = EMPTY_DETAILS

  switch (planType) {
    case 'FRAME':
    case 'LENSES':
      details = getFrameWithLensesBenefitDetails(planType, benefitItem)
      break
  }

  return {
    ...details,
    benefitItem,
  }
}

const getRenewalDetails = (benefitRenewalDate: string): BaseDetails => {
  return {
    isRenewal: true,
  }
}

const getFrameBenefitDetails = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): BaseDetails => {
  const { isStatusAvailable, serviceDescription, showFrameFallbackDescription } = getBenefitServiceDescriptionInfo(
    planType,
    benefitItem
  )

  if (!isStatusAvailable && benefitItem?.benefitRenewalDate) {
    return getRenewalDetails(benefitItem.benefitRenewalDate)
  }

  if (serviceDescription && !showFrameFallbackDescription) {
    return { serviceDescription, isStatusAvailable }
  }

  return {
    isStatusAvailable,
    showFallbackDescription: true,
  }
}

const getLensesBenefitDetails = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): BaseDetails => {
  const { isStatusAvailable, serviceDescription, showLensesFallbackDescription } = getBenefitServiceDescriptionInfo(
    planType,
    benefitItem
  )

  if (!isStatusAvailable && benefitItem?.benefitRenewalDate) {
    return getRenewalDetails(benefitItem.benefitRenewalDate)
  }

  if (serviceDescription && !showLensesFallbackDescription) {
    return { serviceDescription, isStatusAvailable }
  }

  return {
    isStatusAvailable,
    showFallbackDescription: true,
  }
}

const getEyeExamBenefitDetails = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): BaseDetails => {
  const { isStatusAvailable, serviceDescription, benefitService } = getBenefitServiceDescriptionInfo(
    planType,
    benefitItem
  )

  if (!isStatusAvailable && benefitItem?.benefitRenewalDate) {
    return getRenewalDetails(benefitItem.benefitRenewalDate)
  }

  const isCopay = checkBenefitServiceHasCopay(benefitService)

  return {
    isCopay,
    isStatusAvailable,
    serviceDescription,
  }
}

const getGenericBenefitDetails = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): BaseDetails => {
  const { isComingSoon, isStatusAvailable, serviceDescription } = getBenefitServiceDescriptionInfo(
    planType,
    benefitItem
  )

  if (isComingSoon) {
    return {
      isComingSoon,
    }
  }

  if (!isStatusAvailable && benefitItem?.benefitRenewalDate) {
    return getRenewalDetails(benefitItem.benefitRenewalDate)
  }

  return {
    isStatusAvailable,
    serviceDescription,
  }
}

const getFrameWithLensesBenefitDetails = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): BaseDetails => {
  const isCustomerServiceOnly = benefitItem?.benefitAvailability === 'CUSTOMER_SERVICE_ONLY'

  return {
    isStatusAvailable: true,
    isCustomerServiceOnly,
  }
}
